import get from 'lodash/get';
import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import equal from 'fast-deep-equal';
import { matchPath } from 'react-router-dom';

import { HeaderCampaignOff } from 'src/components/Header/HeaderCampaignOff';
import { HeaderFreeTrialBanner } from 'src/components/Header/HeaderFreeTrialBanner';
import { LivestreamStartedBanner } from 'src/components/Header/LivestreamStartedBanner';
import { LivestreamApproachingBanner } from 'src/components/Header/LivestreamApproachingBanner';
import { TestModeBanner } from 'src/components/Header/TestModeBanner';
import { CRoute } from 'src/routes';
import { requiredDataLoaded } from 'src/store/commonPart/selector';
import { commonSlice } from 'src/store/commonPart/slice';
import { customHistory } from 'src/utils/customHistory';
import { getDOMAbsoluteElementHeight, getDOMElement, setDOMElementCss } from 'src/utils/DOM';
import { HeaderNavbar } from './Navbar';

export type HeaderBannerOnShowType = {
  onShow: () => void;
};

export type Props = {
  currentRouteInfo?: CRoute;
};

export const Header: FC<Props> = memo(({ currentRouteInfo }) => {
  const dispatch = useDispatch();

  const appFinishedLoading = useSelector(requiredDataLoaded);

  const headerRef = useRef<number | null>(null);

  const recalcTopOffset = useCallback(
    (unmount = false) => {
      if (headerRef.current === null) {
        /** height won't be changed any longer. just at the start of the App */
        headerRef.current = get(getDOMElement('header'), 'clientHeight', 0);
      }

      /** calculate and store banners height */
      const addBannersHeight = getDOMAbsoluteElementHeight(getDOMElement('.header-banners'));
      let localHeaderHeight = headerRef.current || 0;

      dispatch(
        commonSlice.actions.setCommon({
          addBannersHeight,
          headerHeight: headerRef.current,
        })
      );
      /** end calculate and store banners height */

      /** suppose Header to be unmounted here when there's no pre-reg banner */
      if (unmount && !getDOMElement('.campaign-page-pre-registration-banner')) {
        localHeaderHeight = 0;

        dispatch(
          commonSlice.actions.setCommon({
            headerHeight: localHeaderHeight,
          })
        );
      }

      setDOMElementCss(getDOMElement('#container'), {
        paddingTop: `${addBannersHeight + localHeaderHeight}px`,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (appFinishedLoading) {
      recalcTopOffset();
    }

    return () => {
      if (appFinishedLoading) {
        recalcTopOffset(true);
      }
    };
  }, [recalcTopOffset, appFinishedLoading]);

  const currentRouteBannersInfo: { showLiveStream: boolean; permanentUrl: string; showTestMode: boolean } =
    useMemo(() => {
      let permanentUrl = '';

      const showTestMode = window.shouldShowTestModeBanner || currentRouteInfo?.testModeBannerAvailable || false;

      if (window.liveStreamCampaignInfo) {
        permanentUrl = window.liveStreamCampaignInfo.permanentUrl;
      } else if (currentRouteInfo?.liveStreamBannerAvailable || showTestMode) {
        // strictly in lowercase
        permanentUrl = currentRouteInfo?.path
          ? matchPath<{ permanenturl?: string }>(customHistory.location.pathname, currentRouteInfo.path)?.params
              ?.permanenturl || window.experiencePermanentUrl || ''
          : window.experiencePermanentUrl || '';
      }

      return {
        showLiveStream: window.liveStreamCampaignInfo ? true : currentRouteInfo?.liveStreamBannerAvailable || false,
        showTestMode,
        permanentUrl,
      };
    }, [
      currentRouteInfo?.liveStreamBannerAvailable,
      currentRouteInfo?.path,
      currentRouteInfo?.testModeBannerAvailable,
    ]);

  return (
    <>
      <header className="custom-header">
        <HeaderNavbar />
      </header>

      <div className="header-banners">
        {currentRouteBannersInfo.showLiveStream && (
          <>
            <LivestreamApproachingBanner permanentUrl={currentRouteBannersInfo.permanentUrl} />
            <LivestreamStartedBanner />
          </>
        )}

        {currentRouteBannersInfo.showTestMode && <TestModeBanner permanentUrl={currentRouteBannersInfo.permanentUrl} />}

        <HeaderFreeTrialBanner onShow={recalcTopOffset} />

        {window.isDonationAllowed === false && <HeaderCampaignOff />}
      </div>
    </>
  );
}, equal);
